import {
  DeviceCredentialLostError,
  DeviceDeprovisionedError,
  heartbeat as sendHeartbeat,
  sync,
} from "../api/deviceClient";
import { getBridge } from "../bridge";
import { config } from "../config";
import { prefetchPlaylist } from "../playback/mediaCache";
import { usePlayerStore } from "../state/playerStore";
import { saveScheduleCache } from "../state/scheduleCache";
import { handleCommands } from "./commands";
import { flushPop } from "./popBuffer";

// Owns the three background loops once the device is active: sync (schedule +
// commands), heartbeat, and PoP flush. All are cancellable.

let timers: ReturnType<typeof setInterval>[] = [];
let running = false;

// Invoked when the backend says this device is permanently deprovisioned. The
// runtime registers a handler that wipes credentials and re-enters pairing.
let onDeprovision: (() => void) | null = null;
export function setDeprovisionHandler(fn: () => void): void {
  onDeprovision = fn;
}

// Invoked when the device is still paired server-side but lost its credential. The
// runtime keeps playing from cache and waits for the operator to re-pair it.
let onCredentialLost: (() => void) | null = null;
export function setCredentialLostHandler(fn: () => void): void {
  onCredentialLost = fn;
}

function handleLoopError(where: string, err: unknown): void {
  if (err instanceof DeviceDeprovisionedError) {
    // Permanent: operator unpaired/revoked this device. Stop and re-pair —
    // do NOT keep retrying or playing stale content.
    console.warn(`[syncLoop] ${where}: device deprovisioned, re-entering pairing`);
    stopSyncLoops();
    onDeprovision?.();
    return;
  }
  if (err instanceof DeviceCredentialLostError) {
    // NOT a revocation — keep the schedule, media and proof-of-play. The loops stop
    // (nothing they send can authenticate); playback continues from cache.
    console.warn(`[syncLoop] ${where}: device credential lost, waiting for re-pair`);
    stopSyncLoops();
    onCredentialLost?.();
    return;
  }
  // Transient (network/timeout/5xx): stay online=false and keep playing cache.
  usePlayerStore.getState().setOnline(false);
  console.warn(`[syncLoop] ${where} failed (running from cache)`, err);
}

function serverOffsetFrom(serverTimeIso: string): number {
  const serverMs = new Date(serverTimeIso).getTime();
  return Number.isNaN(serverMs) ? 0 : serverMs - Date.now();
}

async function runSyncOnce(force = false): Promise<void> {
  const store = usePlayerStore.getState();
  try {
    const result = await sync(force ? null : store.etag);
    usePlayerStore.getState().setOnline(true);
    usePlayerStore.getState().markSynced();
    if (!result) return; // 304 — nothing changed

    const screenDefault = result.screen_default ?? null;
    const schedule = {
      screen: result.screen,
      playlist: result.playlist,
      screenDefault,
      etag: result.etag,
      serverOffsetMs: serverOffsetFrom(result.server_time),
    };
    usePlayerStore.getState().setSchedule(schedule);
    // Persist the last-good schedule so a reload during a backend outage can
    // resume from cache instead of blanking until the next successful sync.
    void saveScheduleCache(schedule);

    // The screen default rides in the prefetch groups so it is cached AND kept
    // by the eviction pass (the keep-list is derived from these groups).
    await prefetchPlaylist(result.playlist, screenDefault ? [screenDefault] : []);

    if (result.commands.length > 0) {
      await handleCommands(result.commands, () => void runSyncOnce(true));
    }
  } catch (err) {
    handleLoopError("sync", err);
  }
}

async function runHeartbeatOnce(): Promise<void> {
  const store = usePlayerStore.getState();
  try {
    const storage = await getBridge().getStorageInfo();
    // The reported channel names ARE the wire vocabulary
    // (contracted | bonus | default | idle) — no boundary mapping.
    const play = store.currentPlay;
    const result = await sendHeartbeat({
      status:
        store.playlist.length > 0 || store.screenDefault !== null
          ? "playing"
          : "idle",
      app_version: config.appVersion,
      current_slot_index: store.currentSlotIndex ?? undefined,
      storage_free_bytes: storage.freeBytes,
      current_channel: play ? play.channel : undefined,
      current_booking_id: play?.bookingId ?? undefined,
      current_media_id: play?.mediaId ?? undefined,
    });
    usePlayerStore.getState().setOnline(true);

    // Refresh the clock offset on EVERY beat. The sync path updates it only on
    // a schedule change (304s skip server_time), so without this the offset
    // goes stale for as long as the schedule is stable — and a drifting local
    // clock (VM suspend, sleep) mis-stamps proof-of-play until the backend's
    // trust gate silently drops it. 60s beats keep staleness under the 120s
    // slack. A large jump is worth shouting about: every record stamped since
    // the drift began may have been rejected.
    if (result?.server_time) {
      const newOffset = serverOffsetFrom(result.server_time);
      const jump = Math.abs(newOffset - store.serverOffsetMs);
      if (jump > config.clockJumpWarnMs) {
        console.warn(
          `[syncLoop] clock offset jumped ${Math.round(jump / 1000)}s ` +
            `(${store.serverOffsetMs}ms -> ${newOffset}ms) — local clock drifted; ` +
            "proof-of-play stamped during the drift may have been rejected",
        );
      }
      usePlayerStore.getState().setServerOffset(newOffset);
    }
  } catch (err) {
    handleLoopError("heartbeat", err);
  }
}

export function startSyncLoops(): void {
  if (running) return;
  running = true;

  void runSyncOnce(true);
  void runHeartbeatOnce();
  void flushPop();

  timers.push(setInterval(() => void runSyncOnce(), config.syncIntervalMs));
  timers.push(setInterval(() => void runHeartbeatOnce(), config.heartbeatIntervalMs));
  timers.push(setInterval(() => void flushPop(), config.popFlushIntervalMs));
}

export function stopSyncLoops(): void {
  for (const t of timers) clearInterval(t);
  timers = [];
  running = false;
}
